import React from "react";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import CachedImage from "../../../shared/cached-image/cached-image";
import {cn} from "../../../../utils/utils";

export type TvMediaCardProps = {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    meta?: React.ReactNode;
    tmdbSrc?: string;
    cachedSrc?: string;
    aspectRatio?: "16/9" | "2/3" | "1/1";
    width?: string;
    isSpoiler?: boolean;
    badgeTopLeft?: React.ReactNode;
    badgeTopRight?: React.ReactNode;
    badgeBottomLeft?: React.ReactNode;
    badgeBottomRight?: React.ReactNode;
    showPlayOverlay?: boolean;
    onClick: () => void;
    onSecondaryAction?: () => void;
    secondaryActionLabel?: string;
    onFocusItem?: () => void;
    autoFocus?: boolean;
    className?: string;
};

const TvMediaCard: React.FC<TvMediaCardProps> = ({
    title,
    subtitle,
    meta,
    tmdbSrc,
    cachedSrc,
    aspectRatio = "16/9",
    width,
    isSpoiler = false,
    badgeTopLeft,
    badgeTopRight,
    badgeBottomLeft,
    badgeBottomRight,
    showPlayOverlay = false,
    onClick,
    onSecondaryAction,
    secondaryActionLabel,
    onFocusItem,
    autoFocus,
    className
}) => {
    const defaultWidth =
        aspectRatio === "2/3" ? "210px" : aspectRatio === "1/1" ? "190px" : "330px";

    return (
        <div
            className={cn("tv-media-card-wrapper", className)}
            style={{width: width ?? defaultWidth, flexShrink: 0}}>
            <button
                type="button"
                data-tv-focusable="true"
                data-tv-card-primary="true"
                data-tv-autofocus={autoFocus ? "true" : undefined}
                className="tv-media-card"
                onClick={onClick}
                onFocus={onFocusItem}>
                <div
                    className="tv-media-card__artwork"
                    style={{aspectRatio}}>
                    {tmdbSrc && (
                        <TmdbImage
                            isSpoiler={isSpoiler}
                            src={tmdbSrc}
                            size="w500"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center"
                            }}
                        />
                    )}
                    {!tmdbSrc && cachedSrc && (
                        <CachedImage
                            isSpoiler={isSpoiler}
                            src={cachedSrc}
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center"
                            }}
                        />
                    )}
                    {!tmdbSrc && !cachedSrc && (
                        <div className="tv-media-card__placeholder">
                            <i className="fa-solid fa-film"></i>
                        </div>
                    )}

                    <div className="tv-media-card__badges">
                        {badgeTopLeft && (
                            <span className="tv-media-card__badge tv-media-card__badge--tl">
                                {badgeTopLeft}
                            </span>
                        )}
                        {badgeTopRight && (
                            <span className="tv-media-card__badge tv-media-card__badge--tr">
                                {badgeTopRight}
                            </span>
                        )}
                        {badgeBottomLeft && (
                            <span className="tv-media-card__badge tv-media-card__badge--bl">
                                {badgeBottomLeft}
                            </span>
                        )}
                        {badgeBottomRight && (
                            <span className="tv-media-card__badge tv-media-card__badge--br">
                                {badgeBottomRight}
                            </span>
                        )}
                        {showPlayOverlay && (
                            <span className="tv-media-card__play-icon">
                                <i className="fa-solid fa-play"></i>
                            </span>
                        )}
                    </div>
                </div>

                <div className="tv-media-card__info">
                    {meta && <div className="tv-media-card__meta">{meta}</div>}
                    <div className="tv-media-card__title">{title}</div>
                    {subtitle && <div className="tv-media-card__subtitle">{subtitle}</div>}
                </div>
            </button>

            {onSecondaryAction && (
                <button
                    type="button"
                    data-tv-focusable="true"
                    data-tv-card-secondary="true"
                    className="tv-media-card__secondary-btn"
                    onClick={onSecondaryAction}
                    title={secondaryActionLabel || "Opciones"}>
                    <i className="fa-solid fa-ellipsis"></i>
                    {secondaryActionLabel && <span>{secondaryActionLabel}</span>}
                </button>
            )}
        </div>
    );
};

export default TvMediaCard;
